/**
 * Admin orders API module.
 *
 * Provides typed functions for every admin-facing order endpoint.
 * Callers (containers and Redux thunks) MUST import from this module —
 * never call the shared axios client directly.
 *
 * CON-PAYMENTS-002: Order adjustments (create + delete).
 * CON-SHIPPS-001: Pirate Ship CSV export.
 * CON-SHIPPS-002: Tracking import.
 * CON-ORDER-DETAIL: Order detail fetch + status transition + payment capture.
 */
import { isAxiosError } from 'axios';
import { apiClient } from '@dfq/shared/api/client';
import { throwAsApiError, unwrap } from '@dfq/shared/api/_envelope';
import type { ApiEnvelope } from '@dfq/shared/types/auth';
import type {
  AdminOrderDetailDTO,
  AdminOrderDetailResponseData,
  AdminOrderStatusTransitionResponseData,
  AdminOrderCaptureResponseData,
  AdminOrderStatusTransitionPayload,
  CollectBalanceResultDTO,
  OutstandingBalanceItemDTO,
  OverCapturedItemDTO,
  SetBillableSqftPayload,
  SetBillableSqftResponseData,
} from '@dfq/shared/types/adminOrders';
import type {
  CreateOrderAdjustmentPayload,
  OrderAdjustment,
  OrderAdjustmentResponse,
  OrderAdjustmentsListResponse,
} from '@dfq/shared/types/payments';
import type { TrackingImportResult } from '@dfq/shared/types/trackingImport';

// ---------------------------------------------------------------------------
// Pirate Ship export
// ---------------------------------------------------------------------------

/**
 * Result returned by `exportOrdersToPirateShip` on success or partial success.
 */
export interface PirateShipExportResult {
  blob: Blob;
  /** Filename from Content-Disposition header, or a generated fallback. */
  filename: string;
  /**
   * Orders that were skipped due to missing address or invalid status.
   * Present only when the platform emits a partial-success `unshippable` list.
   */
  unshippable?: Array<{ orderNumber: string; reason: string }>;
}

function parseFilename(header: string | undefined): string {
  if (header) {
    const match = /filename="?([^";]+)"?/.exec(header);
    if (match?.[1]) {
      return match[1].trim();
    }
  }
  const ts = new Date()
    .toISOString()
    .replace(/[:-]/g, '')
    .replace('T', '-')
    .slice(0, 15);
  return `dfq-pirate-ship-export-${ts}.csv`;
}

/**
 * POST `/api/v1/admin/orders/pirate-ship-export` — export selected orders as
 * a Pirate Ship-formatted CSV.
 *
 * Sends `{ order_ids }` as JSON with `responseType: 'blob'`. Axios returns
 * blob error responses as a Blob on 4xx/5xx — this function re-parses the
 * blob text to JSON so `throwAsApiError` can build a typed `ApiError` from
 * the platform envelope.
 *
 * CON-SHIPPS-001. Backend: PL-SHIPPS-001.
 */
export async function exportOrdersToPirateShip(
  orderIds: number[],
): Promise<PirateShipExportResult> {
  try {
    const res = await apiClient.post<Blob>(
      '/admin/orders/pirate-ship-export',
      { order_ids: orderIds },
      { responseType: 'blob' },
    );

    const blob = res.data;
    const filename = parseFilename(res.headers['content-disposition'] as string | undefined);

    let unshippable: PirateShipExportResult['unshippable'];
    const metaHeader = res.headers['x-unshippable-orders'] as string | undefined;
    if (metaHeader) {
      try {
        unshippable = JSON.parse(decodeURIComponent(metaHeader)) as typeof unshippable;
      } catch {
        // Malformed header — ignore.
      }
    }

    return { blob, filename, unshippable };
  } catch (err) {
    // Axios delivers blob error responses as a Blob when responseType:'blob'.
    // Re-parse to JSON so throwAsApiError can extract the platform envelope.
    if (isAxiosError(err) && err.response?.data instanceof Blob) {
      try {
        const text = await (err.response.data as Blob).text();
        const parsed = JSON.parse(text) as ApiEnvelope<unknown>;
        err.response.data = parsed;
      } catch {
        // Not JSON (e.g. a gateway HTML error) — fall through.
      }
    }
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// Order Adjustments
// ---------------------------------------------------------------------------

/**
 * Fetch all adjustments (including soft-deleted) for an order.
 *
 * The list endpoint is not yet implemented on the platform (PL-PAYMENTS-008
 * ships the store/destroy endpoints only). This function is intentionally
 * typed and exported so the container can call it once the list endpoint
 * lands — for now the container derives the list optimistically from local
 * state and this function returns an empty array stub until then.
 *
 * Route: `GET /api/v1/admin/orders/{orderId}/adjustments`
 */
export async function fetchOrderAdjustments(orderId: number): Promise<OrderAdjustment[]> {
  try {
    const res = await apiClient.get<ApiEnvelope<OrderAdjustmentsListResponse>>(
      `/admin/orders/${orderId}/adjustments`,
    );
    const data = unwrap(res.data, res.status);
    return data.adjustments;
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * Create an admin cost adjustment on an order.
 *
 * Route: `POST /api/v1/admin/orders/{orderId}/adjustments`
 *
 * Atomically recomputes tax via `TaxCalculator` and may flip the order's
 * `payment_status` between `authorized` and `requires_action` depending on
 * whether the new total exceeds the authorized amount.
 */
export async function createOrderAdjustment(
  orderId: number,
  payload: CreateOrderAdjustmentPayload,
): Promise<OrderAdjustment> {
  try {
    const res = await apiClient.post<ApiEnvelope<OrderAdjustmentResponse>>(
      `/admin/orders/${orderId}/adjustments`,
      payload,
    );
    const data = unwrap(res.data, res.status);
    return data.adjustment;
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * Soft-delete an order adjustment.
 *
 * Route: `DELETE /api/v1/admin/orders/{orderId}/adjustments/{adjustmentId}`
 *
 * The adjustment row is retained in the audit trail with a `deletedAt`
 * timestamp; it is excluded from order totals. Tax is recomputed atomically.
 */
export async function deleteOrderAdjustment(
  orderId: number,
  adjustmentId: number,
): Promise<void> {
  try {
    await apiClient.delete(
      `/admin/orders/${orderId}/adjustments/${adjustmentId}`,
    );
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// Tracking import
// ---------------------------------------------------------------------------

/**
 * Submit a Pirate Ship "Ship Date Report" CSV for bulk tracking import.
 *
 * Route: `POST /api/v1/admin/orders/tracking-import`
 * Auth:  `auth:console` (admin/super_admin)
 *
 * 60-second timeout — synchronous processing should be sub-second, but
 * allows headroom for large files.
 */
export async function submitTrackingImport(file: File): Promise<TrackingImportResult> {
  const form = new FormData();
  form.append('csv_file', file);
  try {
    const res = await apiClient.post<ApiEnvelope<TrackingImportResult>>(
      '/admin/orders/tracking-import',
      form,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60_000,
      },
    );
    return unwrap(res.data, res.status);
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// Order detail — CON-ORDER-DETAIL
// ---------------------------------------------------------------------------

/**
 * Fetch the full admin order detail.
 *
 * Route: `GET /api/v1/admin/orders/{orderId}`
 * Auth:  `auth:console` (admin/super_admin)
 */
export async function fetchAdminOrderDetail(orderId: number): Promise<AdminOrderDetailDTO> {
  try {
    const res = await apiClient.get<ApiEnvelope<AdminOrderDetailResponseData>>(
      `/admin/orders/${orderId}`,
    );
    const data = unwrap(res.data, res.status);
    return data.order;
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * Transition an order's status.
 *
 * Route: `PATCH /api/v1/admin/orders/{orderId}/status`
 * Body:  `{ status: string }`
 *
 * The platform enforces legal-transition guards server-side. A 409 is
 * returned when the transition is not permitted from the current state.
 * Callers should surface the 409 message as an error toast.
 */
export async function transitionAdminOrderStatus(
  orderId: number,
  payload: AdminOrderStatusTransitionPayload,
): Promise<AdminOrderStatusTransitionResponseData['order']> {
  try {
    const res = await apiClient.patch<ApiEnvelope<AdminOrderStatusTransitionResponseData>>(
      `/admin/orders/${orderId}/status`,
      payload,
    );
    const data = unwrap(res.data, res.status);
    return data.order;
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * Capture an authorized payment for an order.
 *
 * Route: `POST /api/v1/admin/orders/{orderId}/capture`
 *
 * The platform returns a 409 when the payment is not in a capturable state.
 * Callers should surface that message as an error toast.
 */
export async function captureAdminOrderPayment(
  orderId: number,
): Promise<AdminOrderCaptureResponseData['order']> {
  try {
    const res = await apiClient.post<ApiEnvelope<AdminOrderCaptureResponseData>>(
      `/admin/orders/${orderId}/capture`,
    );
    const data = unwrap(res.data, res.status);
    return data.order;
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * Override the billable square footage on a panel line.
 *
 * Route: `POST /api/v1/admin/orders/{orderId}/items/{itemId}/billable-sqft`
 * Body:  `{ billable_sqft, reason }` — the reason is required server-side and
 *        a blank one comes back as a 422 with `errors.reason`.
 *
 * The response carries the calculated figure alongside the override and the
 * recalculated order money. The deposit is untouched by design, so the
 * balance floors at zero rather than going negative.
 *
 * CON-WRAP-106.
 */
export async function setAdminOrderItemBillableSqft(
  orderId: number,
  itemId: number,
  payload: SetBillableSqftPayload,
): Promise<SetBillableSqftResponseData> {
  try {
    const res = await apiClient.post<ApiEnvelope<SetBillableSqftResponseData>>(
      `/admin/orders/${orderId}/items/${itemId}/billable-sqft`,
      payload,
    );
    return unwrap(res.data, res.status);
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// Outstanding balances (PL-DEPOSIT-011)
// ---------------------------------------------------------------------------

/** Snake_case pagination emitted under `meta.pagination`. */
interface WireOutstandingPaginationMeta {
  page: number;
  per_page: number;
  total: number;
  last_page: number;
}

export interface OutstandingBalancesParams {
  page?: number;
  perPage?: number;
  /** `age` (oldest debt first) or `outstanding` (largest first). */
  sort?: 'age' | 'outstanding';
}

export interface OutstandingBalancesResponse {
  orders: OutstandingBalanceItemDTO[];
  total: number;
  page: number;
  perPage: number;
  lastPage: number;
}

/**
 * List orders that reached the press still owing money.
 *
 * Route: `GET /api/v1/admin/orders/outstanding-balances`
 * Scope: `dashboard.payments.view`
 *
 * Pagination lives under the envelope's `meta.pagination`, not in `data`.
 */
export async function fetchOutstandingBalances(
  params: OutstandingBalancesParams = {},
): Promise<OutstandingBalancesResponse> {
  try {
    const res = await apiClient.get<
      ApiEnvelope<{ orders: OutstandingBalanceItemDTO[] }> & {
        meta: { pagination?: WireOutstandingPaginationMeta };
      }
    >('/admin/orders/outstanding-balances', {
      params: {
        page: params.page,
        perPage: params.perPage,
        sort: params.sort,
      },
    });

    const data = unwrap(res.data, res.status);
    const pagination = res.data.meta?.pagination;

    return {
      orders: data.orders ?? [],
      total: pagination?.total ?? 0,
      page: pagination?.page ?? 1,
      perPage: pagination?.per_page ?? 25,
      lastPage: pagination?.last_page ?? 1,
    };
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * Charge whatever an order still owes to the card on file.
 *
 * Route: `POST /api/v1/admin/orders/{orderId}/collect-balance`
 * Scope: `order.payment.force_approve` — collecting moves money, so it needs
 * the same permission as forcing an order through in the first place.
 *
 * `collected: false` means the order owed nothing, which is a success: no
 * Stripe call was made, so a double-click cannot charge twice. A declined card
 * throws with HTTP 402 and the reason under `context.decline_code`.
 */
export async function collectAdminOrderBalance(
  orderId: number,
): Promise<CollectBalanceResultDTO> {
  try {
    const res = await apiClient.post<ApiEnvelope<{ balance: CollectBalanceResultDTO }>>(
      `/admin/orders/${orderId}/collect-balance`,
    );

    return unwrap(res.data, res.status).balance;
  } catch (err) {
    throwAsApiError(err);
  }
}

export interface OverCapturedParams {
  page?: number;
  perPage?: number;
  /** `age` (longest owing first) or `overpaid` (largest first). */
  sort?: 'age' | 'overpaid';
}

export interface OverCapturedResponse {
  orders: OverCapturedItemDTO[];
  total: number;
  page: number;
  perPage: number;
  lastPage: number;
}

/**
 * List orders the customer has overpaid — the mirror of
 * {@link fetchOutstandingBalances}.
 *
 * Route: `GET /api/v1/admin/orders/over-captured`
 * Scope: `dashboard.payments.view`
 *
 * Read-only. No refund is issued from here; each row carries the refundable cap
 * so the console can say when that cap is short of what is owed.
 */
export async function fetchOverCapturedOrders(
  params: OverCapturedParams = {},
): Promise<OverCapturedResponse> {
  try {
    const res = await apiClient.get<
      ApiEnvelope<{ orders: OverCapturedItemDTO[] }> & {
        meta: { pagination?: WireOutstandingPaginationMeta };
      }
    >('/admin/orders/over-captured', {
      params: { page: params.page, perPage: params.perPage, sort: params.sort },
    });

    const data = unwrap(res.data, res.status);
    const pagination = res.data.meta?.pagination;

    return {
      orders: data.orders ?? [],
      total: pagination?.total ?? 0,
      page: pagination?.page ?? 1,
      perPage: pagination?.per_page ?? 25,
      lastPage: pagination?.last_page ?? 1,
    };
  } catch (err) {
    throwAsApiError(err);
  }
}
